import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLoanProducts, useMembers } from '../../../hooks/useApi';
import Pagination from '../../../components/Pagination';
import Swal from 'sweetalert2';
import ApiService from '../../../services/api';
import { formatRupiah } from '@/helpers/utils';

const LoansManagement = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  
  // Memoize API parameters to prevent unnecessary re-renders
  const loansParams = useMemo(() => ({
    search: searchTerm,
    status: statusFilter,
    page: currentPage,
    limit: 10,
    paginate: true
  }), [searchTerm, statusFilter, currentPage]);

  const { data: resLoan, loading, error, refetch } = useLoanProducts(loansParams);

  const loans = resLoan?.data || [];
  const pagination = resLoan?.pagination || {};

  const handleDeleteLoan = async (id) => {
    try {
      const res = await ApiService.deleteLoanProducts(id)
      if (res.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Delete Sukses!'
        });
        refetch()
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to delete data'
      });
    }
  }

  // Handle status change actions
  const handleStatusChange = async (loansId, action) => {
    setIsLoading(true);
    try {
      let response;
      switch (action) {
        case 'approve':
          response = await ApiService.patch(`/members/${loansId}/approve`);
          Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: 'Member approved successfully',
            timer: 2000,
            showConfirmButton: false
          });
          break;
        case 'reject':
          response = await ApiService.patch(`/members/${loansId}/reject`);
          Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: 'Member rejected successfully',
            timer: 2000,
            showConfirmButton: false
          });
          break;
        case 'suspend':
          response = await ApiService.patch(`/members/${loansId}/suspend`);
          Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: 'Member suspended successfully',
            timer: 2000,
            showConfirmButton: false
          });
          break;
        default:
          throw new Error('Invalid action');
      }
      
      // Refetch data to update the UI
      refetch();
    } catch (error) {
      console.error(`Error ${action}ing member:`, error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.response?.data?.message || `Failed to ${action} member`
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get status badge class
  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      true: 'bg-green-100 text-green-800 border-green-300',
      false: 'bg-red-100 text-red-800 border-red-300',
    };
    return badges[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const calculatePercentage = (amount) => {
    return amount * 100 + '%'
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">Error loading loans</div>
          <button 
            onClick={refetch}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Loans Management</h1>
          <p className="text-gray-600">Manage Loans Data</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link
            to="/loans/create"
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded inline-flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Create Loans
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
              Search Loans
            </label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by name, phone, member ID, or occupation..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Status
            </label>
            <select
              id="status"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
      </div>

      {/* Members Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-2 text-gray-600">Loading loans...</span>
          </div>
        ) : loans.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-4">No loans found</div>
            <p className="text-gray-400">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'No Data Found.'
              }
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Produk Pinjaman
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ketentuan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bunga
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loans.map((loan) => (
                    <tr key={loan.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 w-[500px]">
                        <div className=''>
                          <div className="text-sm font-medium text-gray-900">{loan.name} ({loan.code})</div>
                          <div className="text-sm text-gray-500">{loan.description}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-900">Min Plafond : {formatRupiah(loan.minimum_amount)}</div>
                          <div className="text-sm text-gray-900">Max Plafond : {formatRupiah(loan.maximum_amount)}</div>
                          <div className="text-sm text-gray-900">Periode : {loan.min_term_months} - {loan.max_term_months} Bulan</div>
                          <div className="text-sm text-gray-900">Min Simpanan Member : {formatRupiah(loan.min_savings_balance)}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-900">{calculatePercentage(loan.interest_rate)} / { loan.interest_period === 'monthly' ? 'Bulan' : loan.interest_period === 'daily' ? 'Hari' : 'Tahun' }</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <p className={`text-xs rounded-md py-1 px-2 font-medium border text-center ${getStatusBadge(loan.is_active)}`}>{ loan.is_active ? 'Active' : 'Inactive' }</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {/* View Details */}
                          <Link
                            to={`/loans/${loan.id}`}
                            className="text-blue-600 hover:text-blue-900 font-medium"
                          >
                            View
                          </Link>

                          {/* Status Actions */}
                          {!loan.is_active && (
                            <>
                              <button
                                onClick={() => handleStatusChange(loan.id, 'approve')}
                                disabled={isLoading}
                                className="text-green-600 hover:text-green-900 font-medium disabled:opacity-50"
                              >
                                Approve
                              </button>
                            </>
                          )}
                          <Link
                            to={`/loans/${loan.id}/edit`}
                            className="text-amber-600 hover:text-amber-900 font-medium"
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => handleDeleteLoan(loan.id)}
                            className="text-red-600 hover:text-red-900 font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <Pagination
                  currentPage={pagination.current_page}
                  totalPages={pagination.total_pages}
                  totalItems={pagination.total}
                  itemsPerPage={pagination.per_page}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LoansManagement;
